import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Box,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
  InputLabel,
  TextField,
  Typography,
} from "@mui/material";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import Logos from "./Logos";
import Content from "../Layouts/Content";
import Icon from "../../components/Icon";
import pathRoutes from "../../routes/pathRoutes";
import apiService from "../../../lib/services/apiService";
import { validatePassword } from "../../../lib/utils/passwordHelpers";

interface DialogProps {
  data: any;
  handleClose: (value: boolean) => any;
  handleConfirm?: (value: boolean) => any;
  children?: React.ReactNode;
}

interface dataDialogProps {
  component: string;
  title?: string;
  message?: string;
  status?: string;
  size?: "sm" | "md";
  active: boolean;
}

const MessageDialog: React.FC<DialogProps> = ({
  data,
  handleClose,
  handleConfirm,
  children,
}) => {
  const navigate = useNavigate();
  const [dataDialog, setDataDialog] = useState<dataDialogProps>({
    component: "",
    active: false,
  });
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState(" ");

  const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: "white",
      color: "black",
      maxWidth: 500,
      fontSize: "18px",
      border: "1px solid #dadde9",
    },
  }));

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setErrorMessage("");
    const password = e.target.value;
    setPassword(password);
  };

  const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const password = e.target.value;
    setNewPassword(password);
    validatePassword(password, "", setErrorMessage);
  };

  const handleConfirmPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const confirmPassword = e.target.value;
    validatePassword(newPassword, confirmPassword, setErrorMessage);
  };

  const handleSubmitPassword = async () => {
    validatePassword(newPassword, confirmPassword, setErrorMessage);
    if (!errorMessage) {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const response = await apiService<{ message: string }>({
        endpoint: `/users/${user.id}/change-password`,
        method: "PUT",
        data: {
          currentPassword: password,
          newPassword,
          confirmPassword,
        },
      });
      if (response.success) {
        setDataDialog({
          component: "Save",
          title: response.data,
          active: true,
        });
      } else {
        setErrorMessage(response.error);
        setDataDialog({
          component: "Confirmed",
          title: response.error,
          active: true,
        });
      }
    }
  };

  const handleConfirmButton = () => {
    handleConfirm?.(true);
  };

  const handleUserForm = () => {
    navigate(pathRoutes.ADMIN_USUARIOS, { replace: true });
    window.location.reload();
  };

  const Close = () => {
    handleClose(false);
    switch (dataDialog?.component) {
      case "Login":
      case "ResetPassword":
      case "PasswordReset":
        if (dataDialog?.status === "yes") {
          navigate(pathRoutes.ROOT_LOGIN);
        }
        break;
      case "Delete":
        window.location.reload();
        break;
      default:
        if (dataDialog?.status === "yes") {
          navigate(pathRoutes.PORTAL_SAMPLES);
        }
        break;
    }
  };

  const handlePassword = () => {
    handleSubmitPassword();
  };

  const renderLogo = (name: string) => {
    switch (name) {
      case "Login":
      case "ResetPassword":
      case "PasswordReset":
        return (
          <Box className="logos">
            <Logos />
          </Box>
        );
    }
  };

  const renderTitle = (name: string) => {
    switch (name) {
      default:
        return (
          <></> /*<DialogTitle className='header'>
        {renderTitle(dataDialog?.component)}
      </DialogTitle> */
        );
    }
  };

  const renderContent = (name: string) => {
    switch (name) {
      case "Login":
      case "ResetPassword":
      case "PasswordReset":
      case "Confirm":
        return (
          <>
            {dataDialog?.status === "yes" && <Icon name="email-read" />}
            {dataDialog?.title && (
              <Typography className="title">{dataDialog?.title}</Typography>
            )}
            {dataDialog?.message && (
              <DialogContentText className="description">
                {dataDialog?.message}
              </DialogContentText>
            )}
          </>
        );
      case "ChangePassword":
        return (
          <>
            <Content
              header={"Cambiar contraseña"}
              prop={{
                noBorder: "noBorder",
                noMargin: "noMargin",
                noPadding: "noPadding",
              }}
            >
              <Box className="flexCenter">
                <Box className="fields full flexColumnLeft">
                  <Box className="field" sx={{ pt: 3 }}>
                    <InputLabel
                      shrink
                      htmlFor="textfield-password"
                      className="label"
                    >
                      Contraseña actual
                    </InputLabel>
                    <TextField
                      id="textfield-password"
                      type="password"
                      className="text changePassword"
                      placeholder="Escriba su contraseña actual"
                      onChange={handlePasswordChange}
                      required
                    />
                  </Box>
                  <Box className="field" sx={{ pt: 3 }}>
                    <InputLabel
                      shrink
                      htmlFor="textfield-new-password"
                      className="label"
                    >
                      Contraseña nueva
                    </InputLabel>
                    <Box className="flexCenter">
                      <TextField
                        id="textfield-new-password"
                        type="password"
                        className="text changePassword"
                        placeholder="Escriba una nueva contraseña"
                        onChange={handleNewPasswordChange}
                        required
                      />
                      {renderTooltip()}
                    </Box>
                  </Box>
                  <Box className="field" sx={{ pt: 3 }}>
                    <InputLabel
                      shrink
                      htmlFor="textfield-repeat-new-password"
                      className="label"
                    >
                      Repita su contraseña nueva
                    </InputLabel>
                    <Box className="flexCenter">
                      <TextField
                        id="textfield-repeat-new-password"
                        type="password"
                        className="text changePassword"
                        placeholder="Escriba de nuevo su nueva contraseña"
                        onChange={handleConfirmPasswordChange}
                        required
                      />
                      {renderTooltip()}
                    </Box>
                  </Box>
                  {errorMessage && (
                    <p style={{ color: "red" }}>{errorMessage}</p>
                  )}
                </Box>
              </Box>
            </Content>
          </>
        );
      case "Save":
      case "Confirmed":
      case "Delete":
      case "UserForm":
        return (
          <>
            <Box className="flexColumnCenter type">
              <Typography className="title">{dataDialog?.title}</Typography>
              <Box className={`line ${name}`}></Box>
            </Box>
          </>
        );
    }
  };

  const renderActions = (name: string) => {
    switch (name) {
      case "Login":
      case "ResetPassword":
      case "PasswordReset":
        return (
          <Button
            onClick={Close}
            autoFocus
            className="button primary active normalText"
          >
            {" "}
            {dataDialog?.status === "yes" ? "Ir a inicio" : "Volver"}{" "}
          </Button>
        );
      case "ChangePassword":
        return (
          <>
            <Button
              onClick={handlePassword}
              autoFocus
              className="button whiteGreenSecondary normalText"
              disabled={errorMessage ? true : false}
            >
              Aceptar
            </Button>
            <Button
              onClick={Close}
              autoFocus
              className="button whiteGreenSecondary normalText"
            >
              Cancelar
            </Button>
          </>
        );
      case "Confirm":
        return (
          <>
            <Button
              onClick={handleConfirmButton}
              autoFocus
              className="button whiteGreenSecondary normalText"
              type="submit"
            >
              Aceptar
            </Button>
            <Button
              onClick={Close}
              autoFocus
              className="button whiteGreenSecondary normalText"
              variant="outlined"
              sx={{ mr: 2 }}
            >
              Cancelar
            </Button>
          </>
        );
      case "Save":
      case "Confirmed":
      case "Delete":
        return (
          <Button
            onClick={Close}
            autoFocus
            className="button whiteGreenSecondary normalText"
            sx={{ mr: 2 }}
          >
            Aceptar
          </Button>
        );
      case "UserForm":
        return (
          <Button
            onClick={handleUserForm}
            autoFocus
            className="button whiteGreenSecondary normalText"
          >
            Aceptar
          </Button>
        );
    }
  };

  const renderTooltip = () => {
    return (
      <HtmlTooltip
        title={
          <React.Fragment>
            <ul>
              <li>Debe contener mínimo 8 caracteres. </li>
              <li>Estará compuesta por letras mayúsculas y minúsculas.</li>
              <li>Debe contener almenos un símbolo </li>
            </ul>
          </React.Fragment>
        }
      >
        <Button>
          <Icon name="info" />
        </Button>
      </HtmlTooltip>
    );
  };

  useEffect(() => {
    setDataDialog(data);
  }, [data]);

  return (
    <Dialog
      fullWidth={true}
      maxWidth={dataDialog?.size ? dataDialog?.size : "sm"}
      open={dataDialog?.active || false}
      onClose={Close}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className={`dialog`}
    >
      {renderLogo(dataDialog?.component)}
      <Box className={`flexColumnCenter component ${dataDialog?.component}`}>
        {renderTitle(dataDialog?.component)}
        <DialogContent className="content">
          {renderContent(dataDialog?.component)}
        </DialogContent>
        {children}
        <DialogActions className="actions">
          {renderActions(dataDialog?.component)}
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default MessageDialog;
